import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { PracticeAudioEngine } from './audio/practice-audio-engine';
import { parseChart } from './chart/chart-parser';
import type { Chart } from './chart/chart-types';

@customElement('practice-looper')
export class PracticePlayer extends LitElement {
  @property()
  src = '';

  @property()
  title = 'Practice Track';

  @property({ attribute: false })
  chartSource = '';

  @state()
  private playing = false;

  @state()
  private clickEnabled = true;

  @state()
  private currentMeasure = 0;

  @state()
  private currentBeat = 0;

  @state()
  private error?: string;

  private chart?: Chart;
  private engine?: PracticeAudioEngine;
  private animationFrame?: number;

  static styles = css`
    :host {
      display: block;
      max-width: 52rem;
      font-family: system-ui, sans-serif;
    }

    .player {
      padding: 1rem;
      margin-top: 2rem;
      margin-bottom: 2rem;
      border: 1px solid #bbb;
      border-radius: 0.75rem;
    }

    .player .title {
      text-align: center;
    }

    .chart {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 0.5rem;
      margin: 1rem 0;
    }

    .measure {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      min-height: 4.5rem;
      padding: 0;
      border: 2px solid transparent;
      background: #eee;
      cursor: pointer;
    }

    .measure.current {
      border-color: currentColor;
    }

    .beat {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 0;
      font-size: 1.1rem;
    }

    .beat.current {
      background: #78c0ff;
    }

    .controls {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 0.5rem;
      padding: 0.75rem;
    }

    .control {
      background: #e9e9ed;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.45rem;

      min-width: 5.5rem;
      min-height: 3rem;
      padding: 0.5rem 0.75rem;

      color: #222;
      font: inherit;
      font-weight: 600;

      border: 1px solid #77736e;
      border-radius: 0.25rem;

      cursor: pointer;
    }

    .control:active:not(:disabled),
    .control[aria-pressed='true'] {
      box-shadow:
        inset 0 2px 4px rgb(0 0 0 / 20%),
        0 0 0 #77736e;
        background: #d0d0d4;
    }

    .control:focus-visible {
      outline: 3px solid currentColor;
      outline-offset: 3px;
    }

    .control:disabled {
      cursor: not-allowed;
      opacity: 0.45;
      box-shadow: none;
    }

    .control svg {
      width: 1.35rem;
      height: 1.35rem;
      flex: none;
    }

    .control--play {
      min-width: 8rem;
    }

    .control--turnaround {
      min-width: 9rem;
    }

    .control__label {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      line-height: 1.05;
    }

    .control__detail {
      margin-top: 0.2rem;
      font-size: 0.7rem;
      font-weight: 400;
      opacity: 0.7;
    }

    @media (max-width: 32rem) {
      .control {
        flex: 1;
      }

      .control--turnaround {
        flex-basis: 100%;
      }
    }

    button {
      min-height: 2.75rem;
      padding: 0.5rem 1rem;
      font: inherit;
    }

    @media (max-width: 36rem) {
      .chart {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
    }
  `;

  protected willUpdate(): void {
    if (this.chart || !this.chartSource.trim()) {
      return;
    }

    if (!this.src.trim()) {
      this.error = 'The practice player requires an audio source.';
      return;
    }

    try {
      this.chart = parseChart(this.chartSource);

      if (this.chart.measures.length === 0) {
        throw new Error('The chart contains no measures.');
      }

      this.engine = new PracticeAudioEngine(
        this.src,
        this.chart.measures.length,
      );

      void this.engine
        .initialize()
        .catch((error: unknown) => {
          console.error('Practice audio initialization failed:', error);

          this.error =
            error instanceof Error
              ? `Could not load "${this.src}": ${error.message}`
              : `Could not load "${this.src}".`;
        })
        .then(this.goToTurnaround.bind(this));
    } catch (error: unknown) {
      console.error('Chord chart parsing failed:', error);

      this.error =
        error instanceof Error
          ? `Invalid chord chart: ${error.message}`
          : 'The chord chart is invalid.';
    }
  }

  connectedCallback(): void {
    super.connectedCallback();

    const chartElement = this.querySelector<HTMLScriptElement>(
      'script[type="text/plain"][data-chart]',
    );

    this.chartSource = chartElement?.textContent?.trim() ?? '';
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();

    if (this.animationFrame !== undefined) {
      cancelAnimationFrame(this.animationFrame);
    }

    this.engine?.pause();
  }

  render() {
    if (this.error) {
      return html`<p role="alert">${this.error}</p>`;
    }

    return html`
      <section class="player">
        <h2 class="title">${this.title}</h2>

        <div class="chart">
          ${this.chart?.measures.map(
            (measure, measureIndex) => html`
              <button
                class="measure ${
                  measureIndex === this.currentMeasure ? 'current' : ''
                }"
                @click=${() => this.seekToMeasure(measureIndex)}
                aria-label=${`Start at measure ${measureIndex + 1}`}
              >
                ${measure.beats.map(
                  (beat, beatIndex) => html`
                    <span
                      class="beat ${
                        measureIndex === this.currentMeasure &&
                        beatIndex === this.currentBeat
                          ? 'current'
                          : ''
                      }"
                    >
                      ${beat.startsChord ? beat.chord : ''}
                    </span>
                  `,
                )}
              </button>
            `,
          )}
        </div>

        <div class="controls" role="group" aria-label="Playback controls">
          <button
            class="control control--play"
            type="button"
            title=${this.playing ? 'Pause' : 'Play'}
            aria-label=${this.playing ? 'Pause' : 'Play'}
            @click=${this.playing ? this.pause : this.play}
          >
            ${
              this.playing
                ? html`<svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-pause-icon lucide-pause"
                  >
                    <rect x="14" y="3" width="5" height="18" rx="1" />
                    <rect x="5" y="3" width="5" height="18" rx="1" />
                  </svg>`
                : html`<svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-play-icon lucide-play"
                  >
                    <path
                      d="M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z"
                    />
                  </svg>`
            }

            <span>${this.playing ? 'Pause' : 'Play'}</span>
          </button>

          <button
            class="control control--metronome"
            type="button"
            title=${
              this.clickEnabled ? 'Turn metronome off' : 'Turn metronome on'
            }
            aria-pressed=${this.clickEnabled}
            @click=${this.toggleClick}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-metronome-icon lucide-metronome"
            >
              <path d="M12 11.4V9.1" />
              <path d="m12 17 6.59-6.59" />
              <path
                d="m15.05 5.7-.218-.691a3 3 0 0 0-5.663 0L4.418 19.695A1 1 0 0 0 5.37 21h13.253a1 1 0 0 0 .951-1.31L18.45 16.2"
              />
              <circle cx="20" cy="9" r="2" />
            </svg>

            <span class="control__label">
              <span>Click</span>
              <span class="control__detail">
                ${this.clickEnabled ? 'On' : 'Off'}
              </span>
            </span>
          </button>

          <button
            class="control control--turnaround"
            type="button"
            title="Play from two measures before the end"
            @click=${this.restartFromTurnaround}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-undo2-icon lucide-undo-2"
            >
              <path d="M9 14 4 9l5-5" />
              <path
                d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5a5.5 5.5 0 0 1-5.5 5.5H11"
              />
            </svg>

            <span class="control__label">
              <span>Turnaround</span>
              <span class="control__detail">Last 2 measures</span>
            </span>
          </button>
        </div>
      </section>
    `;
  }

  private play = async (): Promise<void> => {
    if (!this.engine) {
      return;
    }
    try {
      await this.engine.play();
      this.playing = true;
      this.updatePosition();
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'Playback failed.';
    }
  };

  private pause = async (): Promise<void> => {
    if (!this.engine) {
      return;
    }

    if (this.playing) {
      this.engine.pause();
      this.playing = false;
      return;
    }
  };

  private toggleClick = (): void => {
    this.clickEnabled = !this.clickEnabled;
    this.engine?.setClickEnabled(this.clickEnabled);
  };

  private seekToMeasure(measureIndex: number): void {
    this.engine?.seekToMeasure(measureIndex);
    this.updatePositionOnce();
  }

  private goToTurnaround(): void {
    if (!this.chart) {
      return;
    }

    this.seekToMeasure(Math.max(0, this.chart.measures.length - 2));
  }

  private restartFromTurnaround(): void {
    this.goToTurnaround();
    this.play();
  }

  private updatePosition = (): void => {
    this.updatePositionOnce();

    if (this.playing) {
      this.animationFrame = requestAnimationFrame(this.updatePosition);
    }
  };

  private updatePositionOnce(): void {
    if (!this.engine) {
      return;
    }

    const position = this.engine.getPosition();

    this.currentMeasure = position.measureIndex;
    this.currentBeat = position.beatIndex;
  }
}
